import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../store/slices/authSlice';
import { ROUTES } from '../Router';

/**
 * InactivityTimer Component
 * 
 * Monitors user activity and automatically logs out the user after a period of inactivity.
 * The timeout duration is configurable via REACT_APP_INACTIVITY_TIMEOUT_MINUTES environment variable.
 * 
 * Features:
 * - Resets timer on any user activity (mouse move, click, keypress, scroll, touch, etc.)
 * - Automatically logs out when timeout expires
 * - Only active when user is logged in
 */
const InactivityTimer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const timeoutRef = useRef(null);
    const eventsRef = useRef([]);

    // Get timeout from environment variable (default: 15 minutes)
    // Convert minutes to milliseconds
    const getTimeoutDuration = () => {
        const minutes = parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES || '15', 10);
        return minutes * 60 * 1000; // Convert to milliseconds
    };

    const handleLogout = () => {
        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Dispatch logout action
        dispatch(logout());

        // Clear localStorage
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');

        // Redirect to login
        navigate(ROUTES.LOGIN, { replace: true });
    };

    const resetTimer = () => {
        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Only set timer if user is logged in
        if (user) {
            // Set new timeout
            timeoutRef.current = setTimeout(() => {
                handleLogout();
            }, getTimeoutDuration());
        }
    };

    useEffect(() => {
        // Only activate timer if user is logged in
        if (!user) {
            // Clear timeout if user is not logged in
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        // Initial timer setup
        resetTimer();

        // List of events that indicate user activity
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
            'keydown',
        ];

        // Add event listeners for user activity
        const handleActivity = () => {
            resetTimer();
        };

        // Attach event listeners
        activityEvents.forEach((event) => {
            window.addEventListener(event, handleActivity, { passive: true });
            eventsRef.current.push({ event, handler: handleActivity });
        });

        // Also listen to visibility change (when user switches tabs/windows)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // User came back to the tab, reset timer
                resetTimer();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        eventsRef.current.push({ event: 'visibilitychange', handler: handleVisibilityChange });

        // Cleanup function
        return () => {
            // Clear timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            // Remove all event listeners
            eventsRef.current.forEach(({ event, handler }) => {
                if (event === 'visibilitychange') {
                    document.removeEventListener(event, handler);
                } else {
                    window.removeEventListener(event, handler);
                }
            });

            eventsRef.current = [];
        };
    }, [user, dispatch, navigate]);

    // This component doesn't render anything
    return null;
};

export default InactivityTimer;
